import React, { ReactNode } from 'react';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

import { useDisclaimers } from '../../hooks/use-disclaimers';

/* === DISCLAIMER PROPS =============================================================== */
interface Props {
  children: ReactNode;
  fromCMS?: boolean;
  name?: string;
  variant: 'important' | 'notice';
}

const Disclaimer = ({ variant, children }: Props) => {
  /* === STATES ======================================================================= */
  const { t } = useTranslation();

  /* === RENDER ======================================================================= */
  return (
    <Box
      mt={2}
      py={2}
      px={4}
      bgcolor={variant === 'important' ? '#FBE8E8' : '#F5F0F7'}
      border={variant === 'important' ? '1.5px solid rgba(207, 30, 60, 1)' : '1.5px solid rgba(63, 42, 85, 1)'}
      borderRadius={2}
    >
      <Stack direction="row" gap="8px" mb={1.5}>
        {variant === 'important' && (
          <Stack direction="column" gap="12px">
            <ErrorRoundedIcon color="primary" />
          </Stack>
        )}
        <Typography color="primary" variant="body1" fontWeight={700}>
          {variant === 'important' && t('important')}
          {variant === 'notice' && t('notice')}
        </Typography>
      </Stack>

      <Stack gap="12px" direction="column">
        {children}
      </Stack>
    </Box>
  );
};

/* === DISCLAIMER CONTENT PROPS ======================================================= */
interface DisclaimerContentProps {
  children: ReactNode;
}

const DisclaimerContent = ({ children }: DisclaimerContentProps) => {
  return (
    <Typography variant="body2" color="secondary.light">
      {children}
    </Typography>
  );
};

/* === DISCLAIMER CMS CONTENT PROPS =================================================== */
interface DisclaimerCMSContentProps {
  name: string;
}

const DisclaimerCMSContent = ({ name }: DisclaimerCMSContentProps) => {
  /* === STATES ======================================================================= */
  const { language } = useI18next();

  const { disclaimers } = useDisclaimers();

  const selectedDisclaimer = disclaimers.find((disclaimer) => disclaimer.name === name);

  if (!process.env.GATSBY_SHOW_DEV) return null;

  if (!selectedDisclaimer || !selectedDisclaimer.enabled || !selectedDisclaimer.forWeb) return null;

  /* === RENDER ======================================================================= */
  return <Typography variant="body2" color="secondary.light" padding={0} margin={0} dangerouslySetInnerHTML={{ __html: selectedDisclaimer[`text_${language}`] }} />;
};

Disclaimer.Content = DisclaimerContent;
Disclaimer.CMSContent = DisclaimerCMSContent;

export { Disclaimer };
